import { Injectable }   from '@angular/core';
import { FormControl, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import * as validatorLib from 'validator';

@Injectable()
export class QuestionControlService {
  constructor() { }

  toFormGroup(template: any, prefill) {
    let group: any = {};
    let page = {};

    let questions = template.questions;
    questions.forEach(question => {
      if (question.tag !== 'title'
        && question.tag !== 'subtitle'
        && question.tag !== 'error'
        && question.tag !== 'fieldError'
      ) {
        let prefill = (page[question.key] !== undefined) ? page[question.key] : question.value;
        group[question.key] = (question.validators && !question.disabled) ?
          new FormControl(prefill !== undefined ? prefill : '', this.getValidators(question)) :
          new FormControl(prefill !== undefined ? prefill : '');
        if (prefill) group[question.key].markAsTouched();
      }
    });

    let formGroup = new FormGroup(group);
    const originalQs = JSON.parse(JSON.stringify(template.questions));
    let dependentMap = {};
    // remember question locations
    template.questions.forEach((question, i) => {
      dependentMap[question.key] = i;
    });

    template.questions.forEach((question, index) => {
      let qc = formGroup.controls[question.key];
      if (qc !== undefined) {
        this.updateDependencies(
          template,
          question,
          dependentMap,
          qc.value,
          originalQs,
          formGroup,
          prefill
        );
        qc.valueChanges.subscribe(data => {
          this.updateDependencies(
            template,
            question,
            dependentMap,
            qc.value,
            originalQs,
            formGroup,
            prefill
          );
        });
        if (question.showField === false && question.tag !== 'title'
          && question.tag !== 'subtitle') {
          qc.setValue('');
        }
      }
      // Error tag trigger
      if (question.tag === 'error') {
        this.triggerComponentError(template, dependentMap, question, formGroup);
      }
      if (question.type === 'origination') {
        this.calculateOriginationFee(question, formGroup);
      }
    });

    return formGroup;
  }

  triggerComponentError(template, dependentMap, question, formGroup) {
    let fields = question.fields;
    question.fields.forEach(field => {
      let qc = formGroup.controls[field.key];
      if (qc) {
        qc.valueChanges.subscribe(data => {
          switch (question.type) {
            case 'age':
              if (!qc.value) break;
              let allFilled = true;
              let date = fields.map(dateField => {
                let dateQc = formGroup.controls[dateField.key];
                if (dateQc.value.length === 0) allFilled = false;
                return dateQc.value;
              }).join('-');
              let younger = (new Date().getTime() - Date.parse(date)) <= question.age * 31556952000;
              // If younger and all values exist show the error
              if (allFilled) {
                question.showField = younger;
                fields.forEach(youngField => {
                  template.questions[dependentMap[youngField.key]].errorTrigger = younger;
                });
              }
              break;

            default:
              question.showField = false;
              break;
          }
        });
      }
    });
  }

  calculateOriginationFee(question, formGroup) {
    let qc = formGroup.controls[question.key];
    if (qc) {
      qc.valueChanges.subscribe(data => {
        question.helper = '<span>Up to $50,000</span><span class="float-right">' +
          'Loan origination fee: $000</span>';
        if (qc.value) this.updateOrigination(qc, question);
      });
    }
    if (qc.value) this.updateOrigination(qc, question);
  }

  updateOrigination(qc, question) {
    let loanAmount = qc.value.toString();
    if (loanAmount.charAt(0) === '$') {
      loanAmount = loanAmount.substr(1, qc.value.length).split(',').join('');
    }
    loanAmount = parseInt(loanAmount, 10);
    if (loanAmount < question.validators.validatorLib[0].min ||
        loanAmount > question.validators.validatorLib[0].max) return;
    let originationFee = loanAmount / 200;
    originationFee = originationFee < question.min ? question.min : originationFee;
    originationFee = originationFee > question.max ? question.max : originationFee;
    question.helper = '<span>Up to $50,000</span><span class="float-right">' +
      'Loan origination fee: $' + originationFee.toString().split('.')[0] + '</span>';
  }

  getValidators(question: any) {
    let validators = question.validators;
    let group: any = [];

    Object.keys(validators).forEach(validatorType => {
      switch (validatorType) {

        // default angular validations (Validators is the Angular class)
        case 'required':
          if (validators[validatorType]) {
            group.push(Validators[validatorType]);
          }
          break;

        // Validation for checkbox
        case 'checked':
          group.push((control: FormControl) => {
            return control.value ? null : 'error';
          });
          break;

        // Custom Validators; validators[validatorType] = options
        case 'validatorLib':
          validators[validatorType].map((validatorObj) => {
            let validatorFnName = validatorObj['fn'];
            // Custom validations need to be pushed as a function onto group
            if (validatorLib[validatorFnName])
              group.push(
                this.validateWrap(
                  validatorLib[validatorFnName],
                  validatorObj,
                  validatorFnName,
                  question
                )
              );
          });
          break;

        default:
          group.push(
            this.validateWrap(
              validators[validatorType]['fn'],
              validators[validatorType],
              validatorType,
              question
            )
          );
          break;
      }
    });

    if (question.tag === 'typeahead' && question.options) {
      group.push(
        (c: FormControl) => {
          if (c.value.trim() === '' || c.value === undefined) {
            return;
          }

          let validOption = false;
          question.options.forEach(option => {
            if (option.value === c.value) {
              validOption = true;
            }
          });

          let message = 'Please choose a valid option.';
          return (validOption) ? null : {
            message: message
          };
        }
      );
    }

    // Wrap multiple validator functions as a single group
    return Validators.compose(group);
  }

  validateWrap(validateFn: Function, options: any, type: string, question: any) {
    let getMessage = this.getMessage;

    // Getting the Form Control allows you to access the value in the field
    return function(c: FormControl) {
      let validateValue = '' + c.value;

      // Special cases
      let fnOptions;
      switch (options.fn) {
        case 'equals':
          fnOptions = options.comparison;
          break;
        case 'isIn':
          fnOptions = options.values;
          break;
        default:
          fnOptions = JSON.parse(JSON.stringify(options));
          break;
      }

      // Sanitize if value is formatted
      if (question && question.format) {
        validateValue = validateValue.replace(/X/g, '0');
        validateValue = validateValue.replace(/[^\d]+/g, '');
      }

      // Adds the options and type to the validator
      if (validateValue.length > 0)
        return validateFn(validateValue, fnOptions) ? null : {
          [type]: false,
          message: options.message ? options.message : getMessage(type, options)
        };
    };
  }

  getMessage(type: string, options: any) {
    switch (type) {
      case 'isInt':
      case 'isFloat':
        return 'Please enter a value between ' + options.min + ' and ' + options.max + '.';
      default:
        return undefined;
    }
  }

  private findCondition(data, condArray) {
    for (let i = 0; i < condArray.length; i++) {
      let toggle = new Function('data', 'return ' + condArray[i]);
      if (toggle(data)) return i;
    }
    return -1;
  }

  private updateDependencies(
    template,
    question,
    dependentMap,
    data,
    originalQuestions,
    formGroup,
    prefill
  ) {
    const dependents = question['dependents'];

    if (dependents) Object.keys(dependents).forEach((depKey) => {
      const dependent = dependents[depKey];
      Object.keys(dependent).forEach((propKey) => {
        const prop = dependent[propKey];

        let condArray = prop.map((individualProp) => individualProp.condition);
        const defaultToggle = new Function('data', 'return ' + condArray.join(' || '));
        let condIndex = this.findCondition(data, condArray);

        let formField = formGroup.controls[depKey];
        let templateDependent = template.questions[dependentMap[depKey]];

        // if (templateDependent && !templateDependent.disabledOnPrefill) {
        if (templateDependent) {
          // truthy condition applies the value to template prop
          if (defaultToggle(data)) {
            if (propKey === 'showField' && templateDependent.tag !== 'title'
              && templateDependent.tag !== 'subtitle') {
              formField.enable();
            }
            if (propKey === 'value') {
              // Fill data from prefill
              if (prop[condIndex].state) {
                let fillData: any = [];
                prop[condIndex].value.forEach(dataObj => {
                  dataObj.fields.forEach(fieldKey => {
                    if (prefill[fieldKey]) fillData.push(prefill[fieldKey]);
                  });
                });
                formField.setValue(fillData.join(' '));
              } else {
                formField.setValue(prop[condIndex].value);
              }
            }
            if (condIndex !== -1) templateDependent[propKey] = prop[condIndex].value;
            if (propKey === 'validators') {
              formField.setValidators(this.getValidators(templateDependent));
              formField.updateValueAndValidity();
            }
          } else {
            if (propKey === 'showField' && templateDependent.tag !== 'title'
              && templateDependent.tag !== 'subtitle') {
              formField.setValue('');
              formField.disable();
            }
            // check to see if the prop even exists by default
            const originalProp = originalQuestions[dependentMap[depKey]][propKey];
            if (originalProp !== undefined) {
              templateDependent[propKey] = originalProp;
              if (propKey === 'validators') {
                formField.setValidators(
                  this.getValidators(originalQuestions[dependentMap[depKey]]));
                formField.updateValueAndValidity();
              }
              // if original does not have prop and current state does, remove it
            } else if (templateDependent[propKey]) delete templateDependent[propKey];
          }
          if (formField && question.tag === 'typeahead' && templateDependent === 'typeahead') {
            formField.updateValueAndValidity();
          }
        }
      });
    });
  }
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
